import React, { useEffect, useState } from "react"
import { View, StyleSheet, Text } from "react-native"
import moment from "moment"
import "moment/locale/fr"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { capitalize } from "../summer/constants/summerContent"
import { formatEuros } from "../brevet/utils/formatEuros"
import { elide } from "@common/utils/activationSuiteDisplay"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import {
    getTemplateName,
    getSlotsFromConfig,
    getSessionsFromConfig,
    getPricingByCount,
    pricingForCount,
    groupSessionsByStart,
    sellsBySeance,
    getPreinscriptionCents,
    sessionFallbackLabel,
} from "./constants/stageContent"

moment.locale("fr")

/**
 * Écran de confirmation post-paiement du funnel Stage : 1 € payé + place
 * réservée + récap des sessions cochées (avec leurs matières) + rappel du solde
 * (prélevé au démarrage, soir du 1ᵉʳ cours — ancré sur la 1ʳᵉ session) + CTA
 * app. L'email de confirmation reste la source de vérité (envoyé par le webhook
 * backend). Les montants affichés viennent du catalogue API (prix du count).
 */
const StageConfirmationScreen = ({ navigation }) => {
    const { state } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable : une ouverture de l'URL de confirmation
    // dans un contexte neuf ré-hydrate via stage/resume (état payé → reste ici).
    const { nextParams } = useStageFunnelGuard()

    useEffect(() => {
        track("stage_funnel_confirmation_viewed")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Config catalogue : nom du stage, sessions (libellés), prix du count.
    const { config } = useStageFunnelConfig(state.funnelStateId)
    const templateName = getTemplateName(config) || state.templateName
    const offeredSessions = getSessionsFromConfig(config)
    const isSeanceUnit = sellsBySeance(config)
    const pricingByCount = getPricingByCount(config)
    const slots = getSlotsFromConfig(config)
    const selectedSlot = slots.find((s) => s.key === state.slotKey) || null

    // Sélection payée : sessions cochées + matières de chacune.
    const selectedSessions = state.sessions || []
    const sessionSubjects = state.sessionSubjects || {}

    // Noms des matières (même source que le configurateur) pour le récap.
    const [catalogSubjects, setCatalogSubjects] = useState([])
    useEffect(() => {
        if (!state.studentClass) return
        let cancelled = false
        stageFunnelService
            .getStageSubjects({
                studentClass: state.studentClass,
                templateSlug: state.templateSlug,
            })
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data ?? res?.data ?? []
                setCatalogSubjects(Array.isArray(list) ? list : [])
            })
            .catch(() => {
                /* les matières du récap sont simplement omises */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.studentClass, state.templateSlug])
    const subjectNameById = new Map(
        catalogSubjects.map((s) => [String(s.id), s.name])
    )
    // Une ligne par SESSION achetée — une semaine en unité semaine, un week-end
    // et ses créneaux en unité créneau. Repli sur les clés quand le catalogue
    // n'est pas encore chargé : une confirmation ne doit jamais s'afficher vide.
    const subjectsLabelFor = (key) =>
        (sessionSubjects[key] || [])
            .map((id) => subjectNameById.get(String(id)))
            .filter(Boolean)
            .join(" + ")

    const selectedOffered = offeredSessions.filter((s) =>
        selectedSessions.includes(s.key)
    )
    const recapSessions = selectedOffered.length
        ? groupSessionsByStart(selectedOffered).map((group) => ({
              date: group.start,
              label: isSeanceUnit
                  ? (group.label ?? group.items[0]?.label)
                  : group.items[0]?.label,
              detail: isSeanceUnit
                  ? group.items.map((i) => i.label).join(" · ")
                  : null,
              subjectsLabel: subjectsLabelFor(group.start),
          }))
        : selectedSessions.map((key, index) => ({
              date: key,
              label: sessionFallbackLabel(
                  { startDate: key.slice(0, 10), endDate: null },
                  index
              ),
              detail: null,
              subjectsLabel: subjectsLabelFor(key),
          }))

    // « Payé aujourd'hui » = la pré-inscription FIXE (1 €) réellement encaissée,
    // et le solde = remainder servi par le backend (repli : total − 1 €) —
    // jamais le deposit_cents catalogue.
    const pricing = pricingForCount(pricingByCount, selectedSessions.length)
    const priceCents = pricing?.priceCents ?? null
    const depositCents = getPreinscriptionCents(config)
    const remainderCents =
        pricing?.remainderAfterPreinscriptionCents ??
        (priceCents != null ? Math.max(0, priceCents - depositCents) : null)

    const firstname = state.studentFirstname || "votre enfant"

    // 1ʳᵉ session cochée (les clés sont triées) — ancre du 1ᵉʳ cours. En unité
    // créneau la clé porte l'heure : on ne garde que la date pour le libellé.
    const firstSessionLabel = selectedSessions.length
        ? capitalize(
              moment(String(selectedSessions[0]).slice(0, 10))
                  .locale("fr")
                  .format("dddd D MMMM")
          )
        : null

    const soldeNote =
        remainderCents != null
            ? `Solde de ${formatEuros(remainderCents / 100)} prélevé le lendemain du 1ᵉʳ cours, une fois la séance tenue`
            : "Solde prélevé le lendemain du 1ᵉʳ cours, une fois la séance tenue — montant détaillé dans l'email de confirmation"

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.confirmation}
            contentContainerStyle={styles.content}
            header={<FunnelStepHeader step={6} total={6} hideBack />}
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() =>
                            navigation.navigate(
                                "stage/app-download",
                                nextParams
                            )
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
        >
            <View style={styles.successIcon}>
                <FontAwesome5
                    name="check"
                    size={32}
                    color={theme.colors.success}
                />
            </View>
            <Text style={styles.title}>C'est confirmé 🎉</Text>
            <Text style={styles.subtitle}>
                La place {elide(firstname)}
                {templateName ? ` au ${templateName}` : " au stage"} est
                réservée.
            </Text>
            <Text style={styles.emailHint}>
                📧 Un email de confirmation vient de partir.
            </Text>

            <View style={styles.summary}>
                <SummaryRow
                    label="Payé aujourd'hui"
                    value={formatEuros(depositCents / 100)}
                />
                {priceCents != null ? (
                    <SummaryRow
                        label={templateName || "Stage"}
                        value={formatEuros(priceCents / 100)}
                        note={soldeNote}
                    />
                ) : (
                    <SummaryRow
                        label={templateName || "Stage"}
                        value=""
                        note={soldeNote}
                    />
                )}
                {recapSessions.map((s, index) => (
                    <SummaryRow
                        key={s.date}
                        label={s.label}
                        // En unité créneau, la valeur porte les créneaux
                        // ET les matières : le parent doit relire ce
                        // qu'il vient d'acheter, pas seulement quand.
                        value={[s.detail, s.subjectsLabel]
                            .filter(Boolean)
                            .join(" · ")}
                        isLast={
                            !selectedSlot && index === recapSessions.length - 1
                        }
                    />
                ))}
                {/* En unité créneau, l'horaire n'est pas un choix que
                    le parent a fait : c'est la grille des créneaux, déjà
                    détaillée ligne par ligne au-dessus. L'afficher ici
                    annoncerait des séances non achetées. */}
                {selectedSlot && !isSeanceUnit ? (
                    <SummaryRow
                        label="Horaire"
                        value={
                            selectedSlot.label ||
                            selectedSlot.sessionLabels.join(" · ")
                        }
                        isLast
                    />
                ) : null}
            </View>

            <View style={styles.block}>
                <Text style={styles.blockKicker}>Prochaines étapes</Text>
                <InfoRow
                    icon="user-friends"
                    text="On attribue un tuteur avant le début du stage."
                />
                <InfoRow
                    icon="mobile-alt"
                    text="Téléchargez l'app Masteur pour suivre les séances."
                />
                <InfoRow
                    icon="calendar-check"
                    text={
                        firstSessionLabel
                            ? `1ᵉʳ cours ${isSeanceUnit ? "le" : "la semaine du"} ${firstSessionLabel} — vous recevez les informations pratiques quelques jours avant.`
                            : "1ᵉʳ cours bientôt — vous recevez les informations pratiques quelques jours avant."
                    }
                />
            </View>
        </FunnelStepLayout>
    )
}

const SummaryRow = ({ label, value, note, isLast }) => (
    <View style={[styles.summaryRow, isLast && { borderBottomWidth: 0 }]}>
        <Text style={styles.summaryLabel}>{label}</Text>
        <View style={{ alignItems: "flex-end", flexShrink: 1 }}>
            {value ? <Text style={styles.summaryValue}>{value}</Text> : null}
            {note ? <Text style={styles.summaryNote}>{note}</Text> : null}
        </View>
    </View>
)

const InfoRow = ({ icon, text }) => (
    <View style={styles.infoRow}>
        <FontAwesome5
            name={icon}
            size={13}
            color={theme.colors.primary}
            style={{ width: 22 }}
        />
        <Text style={styles.infoText}>{text}</Text>
    </View>
)

const styles = StyleSheet.create({
    content: { paddingBottom: 24, alignItems: "center" },
    successIcon: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: theme.colors.successLight,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 14,
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        marginBottom: 6,
        textAlign: "center",
    },
    subtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 14,
        color: theme.colors.grey,
        textAlign: "center",
        marginBottom: 8,
    },
    emailHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        textAlign: "center",
        marginBottom: 18,
    },
    summary: {
        width: "100%",
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        paddingHorizontal: 16,
        marginBottom: 16,
    },
    summaryRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
        gap: 12,
    },
    summaryLabel: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        flex: 1,
    },
    summaryValue: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.dark || theme.colors.primary,
        textAlign: "right",
    },
    summaryNote: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 2,
        textAlign: "right",
    },
    block: { width: "100%", marginTop: 4 },
    blockKicker: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        marginBottom: 8,
        textTransform: "uppercase",
    },
    infoRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
    infoText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.regular || theme.colors.dark,
        flex: 1,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default StageConfirmationScreen
